import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ChevronLeft, ChevronRight, Maximize, X, Wind, Flame, Droplets, ShieldCheck, FileText, Layers,
  MapPin, Calendar, Mail, Phone, Globe, Award,
} from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const SlideShell = ({ children, chapter }) => (
  <div className="flex h-full flex-col px-6 py-8 sm:px-14 sm:py-12">
    <div className="flex items-center gap-3">
      <img src="/logo-mark.png" alt="Norgesconsult" className="h-9 w-auto object-contain" />
      <span className="font-display text-sm font-bold uppercase tracking-[0.28em] text-slate-900">Norgesconsult</span>
      {chapter && (
        <span className="ml-auto font-mono text-xs uppercase tracking-[0.25em] text-sky-700">{chapter}</span>
      )}
    </div>
    <div className="flex flex-1 items-center py-6">{children}</div>
  </div>
);

const ServiceIcon = ({ icon: Icon }) => (
  <div className="rounded-md border border-sky-100 bg-sky-50 p-3 text-sky-700"><Icon size={22} /></div>
);

const slides = [
  {
    chapter: null,
    node: (
      <SlideShell>
        <div className="mx-auto max-w-3xl text-center">
          <img src="/logo-mark.png" alt="Norgesconsult" className="mx-auto h-24 w-auto object-contain" />
          <h1 className="mt-8 font-display text-4xl font-bold tracking-tight text-slate-900 sm:text-6xl">
            Presis VVS-prosjektering for norske bygg
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Rådgivende ingeniører innen varme, kjøling, ventilasjon, sanitær og sprinkler —
            fra konsept til som-bygget dokumentasjon.
          </p>
          <p className="mt-10 font-mono text-xs uppercase tracking-[0.3em] text-sky-700">
            Rådgivende ingeniører · VVS · BIM
          </p>
        </div>
      </SlideShell>
    ),
  },
  {
    chapter: "Hvem vi er",
    node: (
      <SlideShell chapter="Hvem vi er">
        <div className="grid w-full items-center gap-10 lg:grid-cols-2">
          <div>
            <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Spesialister på VVS-prosjektering og BIM
            </h2>
            <p className="mt-5 text-sm leading-relaxed text-slate-600 sm:text-base">
              Norgesconsult AS bistår rådgivere, entreprenører, byggherrer og leverandører med kapasitet
              og spesialistkompetanse — med mål om presise, koordinerte og byggbare leveranser tilpasset
              kundens standarder og prosjektorganisasjon.
            </p>
            <div className="mt-7 flex items-start gap-4 rounded-lg border border-sky-200 bg-sky-50 p-5">
              <Award size={24} className="mt-0.5 shrink-0 text-sky-700" />
              <p className="text-sm leading-relaxed text-slate-700">
                <span className="font-bold text-slate-900">Ansvarlig prosjekterende (PRO).</span> Vi kan påta oss
                ansvarsrett for definerte VVS-ansvarsområder og tiltaksklasser.
              </p>
            </div>
          </div>
          <div className="grid gap-4">
            {[
              ["Teknisk presisjon", "Grundige beregninger gir stabil drift gjennom hele byggets levetid."],
              ["Bærekraftig energi", "Varmegjenvinning, varmepumper og behovsstyrte anlegg."],
              ["Tett samhandling", "Åpne modeller, raske avklaringer og konstruktive tverrfaglige møter."],
            ].map(([t, d]) => (
              <div key={t} className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <p className="font-display text-base font-bold text-slate-900">{t}</p>
                <p className="mt-1 text-sm text-slate-600">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </SlideShell>
    ),
  },
  {
    chapter: "Fagområder",
    node: (
      <SlideShell chapter="Fagområder">
        <div className="w-full">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Komplett VVS-leveranse
          </h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[
              [Wind, "Ventilasjon og luftbehandling", "Luftmengder, aggregater, sjakter og koordinert kanalnett."],
              [Flame, "Varme og kjøling", "Energisentraler, varmepumper, fjernvarme og vannbårne systemer."],
              [Droplets, "Sanitær og VA i bygg", "Tappevann, avløp, overvann og pumpeanlegg."],
              [ShieldCheck, "Brannbeskyttelse", "Sprinkler, hydrant- og slokkeanlegg."],
              [FileText, "Arbeidstegninger", "Klare, koordinerte tegninger – klare for utførelse."],
              [Layers, "BIM og rådgivning", "Revit/MagiCAD, kollisjonsanalyse og BIM-koordinering."],
            ].map(([Icon, t, d]) => (
              <div key={t} className="flex gap-4 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                <ServiceIcon icon={Icon} />
                <div>
                  <p className="font-display text-base font-bold text-slate-900">{t}</p>
                  <p className="mt-1 text-sm text-slate-600">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </SlideShell>
    ),
  },
  {
    chapter: "Prosess",
    node: (
      <SlideShell chapter="Prosess">
        <div className="w-full">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Fra oppstart til godkjent leveranse
          </h2>
          <div className="mt-8 grid gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-5">
            {[
              ["1", "Oppstart og kravavklaring"],
              ["2", "Beregning og systemdesign"],
              ["3", "BIM-modell og koordinering"],
              ["4", "Fagkontroll og publisering"],
              ["5", "Oppfølging og som bygget"],
            ].map(([n, t]) => (
              <div key={n} className="bg-white p-6">
                <span className="font-display text-4xl font-bold text-sky-600">{n}</span>
                <p className="mt-3 text-sm font-semibold text-slate-900">{t}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 flex flex-wrap gap-2">
            {["Beregninger", "BIM-modell", "Skjemaer", "Tegninger", "Mengder", "Kontrollspor"].map((d) => (
              <span key={d} className="rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 font-mono text-xs uppercase tracking-widest text-slate-600">{d}</span>
            ))}
          </div>
        </div>
      </SlideShell>
    ),
  },
  {
    chapter: "Samarbeid",
    node: (
      <SlideShell chapter="Samarbeid">
        <div className="w-full">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Tre samarbeidsmodeller
          </h2>
          <div className="mt-8 grid gap-5 md:grid-cols-3">
            {[
              ["A", "Ressursforsterkning", "Navngitte VVS/BIM-ressurser arbeider i kundens systemer, møter og leveranseplan.", false],
              ["B", "Komplett fagpakke", "Vi leverer et definert VVS-omfang med prosjektledelse, produksjon og intern kontroll.", true],
              ["C", "Produksjon av arbeidstegninger", "Klare, koordinerte arbeidstegninger og detaljer — klare for utførelse.", false],
            ].map(([l, t, d, rec]) => (
              <div key={l} className={`relative rounded-lg border p-7 ${rec ? "border-sky-600/60 bg-white shadow-md" : "border-slate-200 bg-white"}`}>
                {rec && (
                  <span className="absolute -top-3 left-6 rounded-full bg-sky-600 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest text-white">
                    Anbefalt ved tydelig omfang
                  </span>
                )}
                <span className="font-display text-3xl font-bold text-sky-700">{l}</span>
                <p className="mt-3 font-display text-lg font-bold text-slate-900">{t}</p>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </SlideShell>
    ),
  },
  {
    chapter: "Referanser",
    node: (
      <SlideShell chapter="Referanser">
        <div className="w-full">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Dokumentert erfaring
          </h2>
          <div className="mt-8 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            {[
              ["Sandnes brannstasjon – hovedstasjon", "Rogaland brann og redning IKS", "2017", "TK3 · Beredskapsbygg"],
              ["Ny Husnes barneskule", "Kvinnherad kommune", "2025", "Passivhus · Energiklasse A"],
              ["Strand videregående skole", "Rogaland fylkeskommune", "2025–26", "Rehabilitering og nybygg"],
              ["PepsiCo Frito-Lay «Copernicus»", "PepsiCo / Frito-Lay, Polen", "2020–23", "4,2 MW kjølesentral"],
              ["Øyane Helsehamn – legesenter", "Hellvik Eiendom AS", "2024", "Helse · Gulvvarme og kjøling"],
            ].map(([name, client, year, tag]) => (
              <div key={name} className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-6 py-4 last:border-b-0">
                <div>
                  <p className="font-display text-base font-bold text-slate-900">{name}</p>
                  <p className="text-xs text-slate-500">{client}</p>
                </div>
                <div className="flex items-center gap-5">
                  <span className="font-mono text-xs text-slate-500">{year}</span>
                  <span className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-800">{tag}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </SlideShell>
    ),
  },
  {
    chapter: "Referanse",
    node: (
      <SlideShell chapter="Referanse · Sandnes">
        <div className="grid w-full items-center gap-10 lg:grid-cols-2">
          <div className="corner-frame">
            <img src="/projects/brannstasjon.jpg" alt="Sandnes brannstasjon" className="h-72 w-full rounded-sm object-cover sm:h-96" />
          </div>
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-sky-700">Hovedreferanse</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Sandnes brannstasjon – hovedstasjon
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">
              Samfunnskritisk bygg samlokalisert med legevakt og ambulansesentral. Lead-prosjektering
              av varme- og kuldeinstallasjoner (TK3), fagkontroll og teknisk koordinering.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {["ca. 12 700 m²", "ca. 1,08 MW varme", "450 kW varmepumpe/kjøler", "ca. 570 kW kjøling", "Energibrønner og frikjøling"].map((t) => (
                <span key={t} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </SlideShell>
    ),
  },
  {
    chapter: "Referanser",
    node: (
      <SlideShell chapter="Referanser · Utdanning og helse">
        <div className="grid w-full gap-6 lg:grid-cols-3">
          {[
            ["/projects/husnes.jpg", "Ny Husnes barneskule", "Passivhus, energiklasse A. 86 kW varmepumper med bergvarme og frikjøling.", "Kvinnherad · 2025"],
            ["/projects/strand-vgs.png", "Strand videregående skole", "Rehabilitering og nybygg. 305 kW sentral varmeveksler, fem vannbårne systemer.", "Tau · 2025–26"],
            ["/projects/svortland.jpg", "Øyane Helsehamn", "Nytt legesenter med lavtemperatur gulvvarme og lokale kjølesystemer.", "Svortland · 2024"],
          ].map(([img, t, d, m]) => (
            <div key={t} className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
              <img src={img} alt={t} className="h-44 w-full object-cover" />
              <div className="p-5">
                <p className="font-display text-base font-bold text-slate-900">{t}</p>
                <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{d}</p>
                <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-slate-400">{m}</p>
              </div>
            </div>
          ))}
        </div>
      </SlideShell>
    ),
  },
  {
    chapter: "Referanse",
    node: (
      <SlideShell chapter="Referanse · Industri">
        <div className="grid w-full items-center gap-10 lg:grid-cols-2">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-sky-700">Industri · Prosesskjøling</p>
            <h2 className="mt-3 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              PepsiCo Frito-Lay «Copernicus»
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-slate-600 sm:text-base">
              Stort produksjons- og lageranlegg for næringsmiddelproduksjon i Polen — et av Europas mest
              bærekraftige anlegg i sitt slag. HVAC concept design lead, fagkontroll og detaljprosjektering
              av kuldesystemet.
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              {["ca. 54 700 m²", "Kjølesentral ca. 4,2 MW", "2,28 MW absorpsjonskjøler", "2 × 783 kW skruekjølere"].map((t) => (
                <span key={t} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-700">{t}</span>
              ))}
            </div>
          </div>
          <div className="corner-frame order-first lg:order-last">
            <img src="/projects/pepsico.jpg" alt="PepsiCo Frito-Lay anlegg" className="h-72 w-full rounded-sm object-cover sm:h-96" />
          </div>
        </div>
      </SlideShell>
    ),
  },
  {
    chapter: "Team",
    node: (
      <SlideShell chapter="Team">
        <div className="w-full">
          <h2 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Ledelsen</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2">
            {[
              ["/team/mads.png", "Mads A. Kjærås", "Daglig leder", "Mange års ledererfaring fra VVS- og industrisektoren i Norge, med fokus på tekniske løsninger, energi og effektiv prosjektleveranse for bygg."],
              ["/team/marek.png", "Marek Piotrowicz", "Faglig leder", "30 års erfaring fra HVAC/VVS-prosjektering i Europa, med ledelse av tverrfaglige ingeniørteam og leveranser til industrielle og kommersielle bygg."],
            ].map(([img, name, role, bio]) => (
              <div key={name} className="flex gap-6 rounded-lg border border-slate-200 bg-white p-7 shadow-sm">
                <img src={img} alt={name} className="h-24 w-24 shrink-0 rounded-full border-2 border-sky-100 object-cover" />
                <div>
                  <p className="font-display text-lg font-bold text-slate-900">{name}</p>
                  <p className="mt-0.5 font-mono text-xs font-semibold uppercase tracking-widest text-sky-700">{role}</p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </SlideShell>
    ),
  },
  {
    chapter: null,
    node: (
      <SlideShell>
        <div className="mx-auto max-w-2xl text-center">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-sky-700">Takk for oss</p>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-5xl">
            La oss diskutere ditt neste prosjekt
          </h2>
          <div className="mx-auto mt-10 grid max-w-md gap-3 text-left">
            {[
              [Mail, "post@norgesconsult.no"],
              [Phone, "+47 412 47 400"],
              [Globe, "www.norgesconsult.no"],
            ].map(([Icon, v]) => (
              <div key={v} className="flex items-center gap-4 rounded-lg border border-slate-200 bg-white px-5 py-4 shadow-sm">
                <div className="rounded-md border border-sky-100 bg-sky-50 p-2.5 text-sky-700"><Icon size={18} /></div>
                <span className="font-display text-base font-semibold text-slate-900">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </SlideShell>
    ),
  },
];

const Presentation = () => {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const total = slides.length;

  const go = useCallback(
    (next) => {
      setDir(next > index ? 1 : -1);
      setIndex(((next % total) + total) % total);
    },
    [index, total]
  );

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " " || e.key === "PageDown") { e.preventDefault(); go(index + 1); }
      if (e.key === "ArrowLeft" || e.key === "PageUp") { e.preventDefault(); go(index - 1); }
      if (e.key === "Home") go(0);
      if (e.key === "End") go(total - 1);
      if (e.key.toLowerCase() === "f") {
        if (document.fullscreenElement) document.exitFullscreen();
        else document.documentElement.requestFullscreen().catch(() => {});
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, index, total]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-white" data-testid="presentation-mode">
      <div className="h-1 w-full bg-slate-100">
        <div className="h-full bg-sky-600 transition-all duration-500" style={{ width: `${((index + 1) / total) * 100}%` }} data-testid="presentation-progress" />
      </div>

      <div className="relative flex-1 overflow-hidden">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={index}
            className="absolute inset-0"
            initial={{ opacity: 0, x: dir * 70 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir * -70 }}
            transition={{ duration: 0.45, ease: EASE }}
          >
            {slides[index].node}
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-between border-t border-slate-200 px-6 py-3 sm:px-14">
        <span className="font-mono text-xs uppercase tracking-widest text-slate-400" data-testid="presentation-counter">
          {String(index + 1).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>
        <div className="flex items-center gap-2">
          <button onClick={() => go(index - 1)} data-testid="presentation-prev" aria-label="Forrige lysbilde"
            className="rounded-md border border-slate-300 p-2 text-slate-600 transition-colors hover:border-sky-600 hover:text-sky-700">
            <ChevronLeft size={18} />
          </button>
          <button onClick={() => go(index + 1)} data-testid="presentation-next" aria-label="Neste lysbilde"
            className="rounded-md border border-slate-300 p-2 text-slate-600 transition-colors hover:border-sky-600 hover:text-sky-700">
            <ChevronRight size={18} />
          </button>
          <button
            onClick={() => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen().catch(() => {}))}
            data-testid="presentation-fullscreen" aria-label="Fullskjerm"
            className="rounded-md border border-slate-300 p-2 text-slate-600 transition-colors hover:border-sky-600 hover:text-sky-700"
          >
            <Maximize size={18} />
          </button>
          <Link to="/" data-testid="presentation-exit" aria-label="Avslutt presentasjon"
            className="rounded-md border border-slate-300 p-2 text-slate-600 transition-colors hover:border-red-300 hover:text-red-600">
            <X size={18} />
          </Link>
        </div>
        <span className="hidden font-mono text-[10px] uppercase tracking-widest text-slate-400 sm:block">
          Piltaster · F = fullskjerm
        </span>
      </div>
    </div>
  );
};

export default Presentation;
